import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  fetchClients,
  fetchClientsAll,
  createClient,
  updateClient,
  deleteClient,
} from "./thunks";
import { Client } from "@/types";
import { toast } from "react-toastify";

interface PaginatedClients {
  data: Client[];
  total?: number;
  currentPage?: number;
  perPage?: number;
}

interface ClientsState {
  clients: PaginatedClients;
  clientsAll: Client[]; // Lista de todos os clientes sem paginação
  loading: boolean;
  loadingAll: boolean; // Loading específico para clientsAll
  error: string | null;
  fetched: boolean;
}

const initialState: ClientsState = {
  clients: { data: [] },
  clientsAll: [], // Lista de todos os clientes sem paginação
  loading: false,
  loadingAll: false, // Loading específico para clientsAll
  error: null,
  fetched: false,
};

const ClientSlice = createSlice({
  name: "clients",
  initialState,
  reducers: {
    clearSuppliers: (state) => {
      state.clients = { data: [] };
      state.loading = false;
      state.error = null;
      state.fetched = false;
    },
    clearClientsAll: (state) => {
      state.clientsAll = [];
      state.loadingAll = false;
    },
    resetFetched: (state) => {
      state.fetched = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchClients.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchClients.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.fetched = true;
        const payload = action.payload;

        if (Array.isArray(payload.data)) {
          // Dados não paginados
          state.clients = { data: payload.data };
        } else {
          // Dados paginados
          state.clients = {
            data: payload.data.data,
            total: payload.data.total,
            currentPage: payload.data.current_page,
            perPage: payload.data.per_page,
          };
        }
      })
      .addCase(fetchClients.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading = false;
        state.fetched = false;
      })
      // Fetch Clients All
      .addCase(fetchClientsAll.pending, (state) => {
        state.loadingAll = true;
        state.error = null;
      })
      .addCase(
        fetchClientsAll.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loadingAll = false;
          const payload = action.payload;
          // Como é without_pagination, sempre será um array direto
          state.clientsAll = payload.data || [];
        },
      )
      .addCase(fetchClientsAll.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loadingAll = false;
      })
      // Create
      .addCase(createClient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createClient.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.clients.data.push(action.payload.data);
        // Também adicionar ao clientsAll se existir
        state.clientsAll.push(action.payload.data);
        toast.success("Cliente cadastrado com sucesso!");
      })
      .addCase(createClient.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as { message: string }).message;
        toast.error(
          (action.payload as { message: string }).message ||
            "Não foi possível cadastrar o cliente, tente novamente!",
        );
      })

      // Update
      .addCase(updateClient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateClient.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        const index = state.clients.data.findIndex(
          (s) => s.id == action.payload.data.id,
        );
        if (index !== -1) {
          state.clients.data[index] = action.payload.data;
        }
        // Também atualizar no clientsAll se existir
        const indexAll = state.clientsAll.findIndex(
          (s) => s.id == action.payload.data.id,
        );
        if (indexAll !== -1) {
          state.clientsAll[indexAll] = action.payload.data;
        }
        toast.success("Cliente atualizado com sucesso!");
      })
      .addCase(updateClient.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as { message: string }).message;
        toast.error(
          (action.payload as { message: string }).message ||
            "Não foi possível atualizar o cliente, tente novamente!",
        );
      })

      // Delete
      .addCase(deleteClient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteClient.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.clients.data = state.clients.data.filter(
          (s) => String(s.id) != action.payload.data.id,
        );
        // Também remover do clientsAll
        state.clientsAll = state.clientsAll.filter(
          (s) => String(s.id) != action.payload.data.id,
        );
        toast.success("Cliente excluído com sucesso!");
      })
      .addCase(deleteClient.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading = false;
      });
  },
});

export const { clearSuppliers, clearClientsAll, resetFetched } =
  ClientSlice.actions;
export default ClientSlice.reducer;
